import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Response } from '../../../_shared/models';
import { KpiStats } from "../models/kpi-stats.model";
import { Subject, firstValueFrom, takeUntil } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ExecutiveStatsRequest, ExecutiveStatsSummaryRequest } from "../../../_shared/models/driver-summary-request.model";
import { KpiStatsViolations } from "../models/kpi-stats-violations.model";
import { KpiStatsInsightsSummary } from "../models/kpi-stats-insights-summary.model";
import { KpiStatsInsights } from "../models/kpi-stats-insights.model";
import { KpiStatsDriverScores } from "../models/kpi-stats-driver-scores.model";


@Injectable({
  providedIn: 'root'
})

export class ExecutiveStatsService {

  constructor(private http: HttpClient) { }

  public getCostPerKilometer(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStats>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpistats/cost-per-km`, request).pipe(takeUntil(cancellation)));
  }

  public getFuelConsumption(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStats>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpistats/fuel-consumption`, request).pipe(takeUntil(cancellation)));
  }

  public getOvertimeHours(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStats>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpistats/overtime-hours`, request).pipe(takeUntil(cancellation)));
  }

  public getFuelVariance(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStats>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpistats/fuel-variance`, request).pipe(takeUntil(cancellation)));
  }

  public getViolations(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsViolations[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/violations`, request).pipe(takeUntil(cancellation)));
  }

  public getViolationsInsightsSummary(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsightsSummary>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/violations-summary`, request).pipe(takeUntil(cancellation)));
  }

  public getGlobalViolations(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsViolations[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/global/violations`, request).pipe(takeUntil(cancellation)));
  }

  public getDistanceDriven(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsights[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/distance-driven-time-series`, request).pipe(takeUntil(cancellation)));
  }

  public getDistanceDrivenInsightsSummary(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsightsSummary>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/distance-driven-summary`, request).pipe(takeUntil(cancellation)));
  }

  public getGlobalDistanceDriven(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsights[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/global/distance-driven-time-series`, request).pipe(takeUntil(cancellation)));
  }

  public getIdleTime(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsights[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/idle-time-series`, request).pipe(takeUntil(cancellation)));
  }

  public getIdleTimeInsightsSummary(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsightsSummary>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/idle-time-summary`, request).pipe(takeUntil(cancellation)));
  }

  public getGlobalIdleTime(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsights[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/global/idle-time-series`, request).pipe(takeUntil(cancellation)));
  }

  public getDriverUtilization(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsights[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/driver-utilization-time-series`, request).pipe(takeUntil(cancellation)));
  }

  public getDriverUtilizationInsightsSummary(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsightsSummary>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/driver-utilization-summary`, request).pipe(takeUntil(cancellation)));
  }

  public getGlobalDriverUtilization(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsights[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/global/driver-utilization-time-series`, request).pipe(takeUntil(cancellation)));
  }

  public getTrips(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsights[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/trips-time-series`, request).pipe(takeUntil(cancellation)));
  }

  public getTripInsightsSummary(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsightsSummary>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/trips-summary`, request).pipe(takeUntil(cancellation)));
  }

  public getGlobalTrips(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsights[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/global/trips-time-series`, request).pipe(takeUntil(cancellation)));
  }

  public getBestDriverScores(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsDriverScores[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/best-driver-scores`, request).pipe(takeUntil(cancellation)));
  }

  public getWorstDriverScores(request: ExecutiveStatsRequest, cancellation: Subject<void>): Promise<Response<KpiStatsDriverScores[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/worst-driver-scores`, request).pipe(takeUntil(cancellation)));
  }

  public getDriverScoresInsightsSummary(request: ExecutiveStatsSummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsInsightsSummary>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/executive-stats/kpiinsights/driver-scores-summary`, request).pipe(takeUntil(cancellation)));
  }
}
